import parse from "html-react-parser";
import { forwardRef, useEffect, useRef, useState } from "react";
import { useReducedProductsPrice } from "@/func/productCardHelpers.js";
import { useCartData } from "@/zustand/cartStore.js";
import { useProductData } from "@/zustand/productStore.js";
import ProductSelectorButton from "@/components/ProductSelectorButton";
import ProductPriceBlock from "@/components/ProductPackages/PriceBlock";
import DoubleTagIcon from "@/public/fallbacks/DoubleTagIcon.svg";
import { isDiscountActive } from "@/utils/discounts";

const productsContent = window?.post_template.checkout_v4_products;
const cardBackground = productsContent.checkout_v4_products_CardBackground;
const cardBackgroundSelected = productsContent.checkout_v4_products_selectedCardBackground;
const cardBorder = productsContent.checkout_v4_products_borderColor;
const cardBorderSelected = productsContent.checkout_v4_products_borderColorSelected;
const textPrimary = productsContent.checkout_v4_products_textPrimary;
const textSecondary = productsContent.checkout_v4_products_textSecondary;
const outlineBorder = productsContent.checkout_v4_products_imageWrap;
const showBadges = productsContent.checkout_v4_products_badgeShow;
const textSale = productsContent?.checkout_v4_products_textSale;
const textBestValue = productsContent?.checkout_v4_products_bestValue;
const textBestSeller = productsContent?.checkout_v4_products_bestSeller;
const textBadge = productsContent?.checkout_v4_products_badgeTextColor;
const cardButtonColor = window?.post_template?.checkout_v4_products?.checkout_v4_products_checkButtonCcColor;

document.documentElement.style.setProperty("--checkbutton-cc-color", cardButtonColor);
document.documentElement.style.setProperty("--card-background-active", cardBackgroundSelected);
document.documentElement.style.setProperty("--card-border-active", cardBorderSelected);
document.documentElement.style.setProperty("--card-border", cardBorder);
document.documentElement.style.setProperty("--card-background", cardBackground);
document.documentElement.style.setProperty("--card-text-primary", textPrimary);
document.documentElement.style.setProperty("--card-text-secondary", textSecondary);
document.documentElement.style.setProperty("--sale-text", textSale);
document.documentElement.style.setProperty("--best-value-text", textBestValue);
document.documentElement.style.setProperty("--best-seller-text", textBestSeller);
document.documentElement.style.setProperty("--text-badge", textBadge);

const ProductBundleItem = forwardRef((props, ref) => {
  const { product, handleChange, active, index } = props;
  const productData = useProductData();
  const { discountCode, isSubscribe } = useCartData();
  const [render, setRender] = useState();
  const [fadeIn, setFadeIn] = useState();
  const variantsRef = useRef();
  const isBestSeller = product.best_seller;
  const isBestValue = product.best_value;

  const productInfo = useReducedProductsPrice(product, productData?.currency_code, productData.formatted_string);

  const discountActive = isDiscountActive(productData, isSubscribe, discountCode);
  const productPrice = discountActive ? productInfo.promoPriceObject : productInfo.priceObject;
  const discountPercent = discountActive ? product?.promo_discount_amount_percent : product.you_save_percentage;

  const fallbackImages = window.post_template.checkout_v4_products.checkout_v4_product_images;
  const matchingImage = fallbackImages[index]?.product_image;
  const image = matchingImage ? matchingImage : product.image.url;

  useEffect(() => {
    if (active) {
      setRender(true);
      setTimeout(() => {
        setFadeIn(active);
      }, 300);
    } else {
      setFadeIn(false);
      setTimeout(() => {
        setRender(false);
      }, 150);
    }
  }, [active, render]);

  return (
    <div
      className={`productPackages__containerWrapper ${
        active ? "productPackage__container--visible" : "productPackages__container--unchecked"
      }`}
    >
      <div
        data-testid="product-container"
        onClick={() => {
          handleChange(index);
        }}
        id={`productCard-${product.amount}`}
        className="productPackages__container bundles"
      >
        <div className="productPacItem__topBlock">
          <ProductSelectorButton additionalClasses="bundles" />
          {product.bundle_title.length > 0 && productData.show_bundle_design ? (
            <div
              className="productPacItem__label"
              dangerouslySetInnerHTML={renderBundleTitle(product.bundle_title)}
            ></div>
          ) : (
            <>
              <span data-testid="product-amount" className="productPacItem__label__amount">
                {product.amount}x
              </span>
              &nbsp;–&nbsp;
              <span className="productPacItem__label">{product.display_name}</span>
            </>
          )}

          <div className="productPacItem__discount__box desktopHide" style={!showBadges ? { opacity: "0" } : {}}>
            <img src={DoubleTagIcon} /> {discountPercent}%
          </div>
        </div>
        <div className="separator"></div>
        <div className="productPacItem__firstBlock">
          <div>
            <img
              src={image}
              className={`productPacItem__image ${outlineBorder ? "productPacItem__image--border" : ""}`}
              alt={product.image.alt}
            />
          </div>
        </div>
        <div className="productPacItem__divider mobileHide"></div>
        <div className="productPacItem__discount__bundlesContainer mobileHide">
          <div className="productPacItem__discount__box" style={!showBadges ? { opacity: "0" } : {}}>
            <img src={DoubleTagIcon} />
            {discountPercent}%
          </div>
        </div>
        <div className="productPacItem__divider tabletHide smallMobileHide"></div>

        <ProductPriceBlock
          isBestValue={isBestValue}
          isBestSeller={isBestSeller}
          oldPrice={productInfo.oldPrice}
          priceObject={productPrice}
          product={product}
          bundledPrices={true}
          bundle={true}
        />
      </div>
      {render && product?.tr_bundle_products && (
        <div
          className={`variants-container ${fadeIn ? "variants-container--show" : "variants-container--hide"}`}
          style={fadeIn ? { maxHeight: `${variantsRef?.current?.clientHeight}px` } : { maxHeight: "0px" }}
        >
          <div ref={variantsRef}>
            <section className="variant-container">
              <div className="variant-seperator"></div>
              <div className="variant-container__wrapper">
                <div className="bundle-label">{window.post_template?.checkout_v4_products?.tr_bundle_block_label}</div>
                <div className="bundle-products">{parse(product?.tr_bundle_products)}</div>
              </div>
            </section>
          </div>
        </div>
      )}
    </div>
  );
});

function renderBundleTitle(bundleTitle) {
  return { __html: bundleTitle };
}

export default ProductBundleItem;
